import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Subject } from 'rxjs';
import { CustomerService } from 'src/app/_services/customer.service';
import { InsuranceFlowService } from 'src/app/_services/insurance-flow.service';
import { RoleService } from 'src/app/_services/role.service';
import { User } from 'src/app/_types/auth.types';
import { InsuranceFlowDetailsResponse } from 'src/app/_types/insurance-flow.types';
import { Lead } from 'src/app/_types/lead.types';
import { TokenPartner } from 'src/app/_types/partner.type';
import { ProposalDetails } from 'src/app/_types/proposals.type';
import { FamilyMemberType } from 'src/app/enums/family-member-type.enum';
import { InsuranceType } from 'src/app/enums/insurance-type.enum';
import { PartnerType } from 'src/app/enums/partner.enums';
import { Roles } from 'src/app/enums/roles.enum';
import { Globals } from 'src/app/globals';

export type TaskDetailsState = {
  isLoading: boolean;
  insuranceFlowData: InsuranceFlowDetailsResponse | null;
  userProfile: User | null;
  lead: Lead | null;
  proposals: ProposalDetails[];
  policy: ProposalDetails | null;
  familyMemberName: string | null;
  relation: FamilyMemberType | null;
  insuranceType: InsuranceType | null;
  canEdit: boolean;
};

const INITIAL_STATE: TaskDetailsState = {
  isLoading: false,
  insuranceFlowData: null,
  userProfile: null,
  lead: null,
  proposals: [],
  policy: null,
  familyMemberName: null,
  relation: null,
  insuranceType: null,
  canEdit: false,
};

/**
 * Mediator that centralizes task-details state and cross-component actions.
 */
@Injectable()
export class TaskDetailsService {
  private readonly stateSubject = new BehaviorSubject<TaskDetailsState>(
    INITIAL_STATE
  );

  private readonly loadErrorSubject = new Subject<HttpErrorResponse>();

  private lastRequestedFlowId: string | null = null;

  /**
   * Something the sheet does not own changed for this affaire — a cancellation
   * opened on its policy, say. The flow's own status does not move, so a host
   * watching the status would never hear about it.
   */
  private readonly changedSubject = new Subject<void>();

  readonly state$ = this.stateSubject.asObservable();

  readonly changed$ = this.changedSubject.asObservable();

  /**
   * Emits when the flow details request fails. The host decides how to react:
   * the modal just shows an empty state, the page redirects away.
   */
  readonly loadError$ = this.loadErrorSubject.asObservable();

  constructor(
    private readonly insuranceFlowService: InsuranceFlowService,
    private readonly customerService: CustomerService,
    private readonly roleService: RoleService,
    private readonly globals: Globals,
    private readonly destroyRef: DestroyRef
  ) {
    this.resolveEditPermission();
  }

  get state(): TaskDetailsState {
    return this.stateSubject.value;
  }

  clearTaskDetails(): void {
    this.lastRequestedFlowId = null;
    // canEdit depends on the logged-in user, not on the loaded flow
    this.stateSubject.next({ ...INITIAL_STATE, canEdit: this.state.canEdit });
  }

  /** Tells whoever hosts the sheet that this affaire reads differently now. */
  notifyChanged(): void {
    this.changedSubject.next();
  }

  refreshTaskDetails(): void {
    if (!this.lastRequestedFlowId) {
      return;
    }

    this.loadTaskDetails(this.lastRequestedFlowId);
  }

  loadTaskDetails(flowId: string): void {
    this.lastRequestedFlowId = flowId;
    this.updateState({ isLoading: true });

    this.insuranceFlowService.getInsuranceFlowDetails(flowId).subscribe({
      next: (flowDetailsResponse) => {
        if (this.lastRequestedFlowId !== flowId) {
          return;
        }

        const insuranceFlowData = flowDetailsResponse?.data ?? null;
        if (!insuranceFlowData) {
          this.clearTaskDetails();
          return;
        }

        this.updateState({
          insuranceFlowData,
          lead: insuranceFlowData.lead ?? null,
          proposals: insuranceFlowData.proposals ?? [],
          policy: insuranceFlowData.policy ?? null,
          familyMemberName: insuranceFlowData.memberName ?? null,
          relation: insuranceFlowData.lead?.relation ?? null,
          insuranceType:
            insuranceFlowData.insuranceType ??
            insuranceFlowData.lead?.insuranceType ??
            null,
        });

        const memberId = insuranceFlowData.userId;
        if (!memberId) {
          this.updateState({ userProfile: null, isLoading: false });
          return;
        }

        this.customerService.getUserDetails(memberId).subscribe({
          next: (userResponse) => {
            if (this.lastRequestedFlowId !== flowId) {
              return;
            }

            this.updateState({
              userProfile: userResponse?.data ?? null,
              isLoading: false,
            });
          },
          error: () => {
            if (this.lastRequestedFlowId !== flowId) {
              return;
            }

            this.updateState({
              userProfile: null,
              isLoading: false,
            });
          },
        });
      },
      error: (error: HttpErrorResponse) => {
        if (this.lastRequestedFlowId !== flowId) {
          return;
        }

        this.clearTaskDetails();
        this.loadErrorSubject.next(error);
      },
    });
  }

  private resolveEditPermission(): void {
    const isPartner =
      (this.globals.getUser() as TokenPartner)?.userType ===
      PartnerType.PARTNER;

    if (isPartner) {
      this.updateState({ canEdit: true });
      return;
    }

    this.roleService
      .hasWritePermission(Roles.PROPOSALS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (hasWritePermission) => {
          this.updateState({ canEdit: hasWritePermission });
        },
      });
  }

  private updateState(partialState: Partial<TaskDetailsState>): void {
    this.stateSubject.next({
      ...this.state,
      ...partialState,
    });
  }
}
